import { MedicalTitleName } from '@caresend/types';
import { parsePhoneNumber } from 'libphonenumber-js';
import { capitalize } from './string.js';

/** Add . after middle initial if value of `middleNames` is one char. */

const formatMiddleNames = middleNames => (middleNames === null || middleNames === void 0 ? void 0 : middleNames.length) === 1 ? `${middleNames}.` : middleNames;

const formatFullName = (user, withMiddle = false, withTitle = true, fallbackName = 'Unknown Name',
/** Example: "Julie S" (if withMiddle and withTitle are false) */
lastInitial = false) => {
  var _a, _b, _c, _d;

  const {
    firstName,
    middleNames,
    lastName
  } = (_a = user === null || user === void 0 ? void 0 : user.info) !== null && _a !== void 0 ? _a : {};
  const title = (_c = (_b = user === null || user === void 0 ? void 0 : user.professionalInfo) === null || _b === void 0 ? void 0 : _b.title) !== null && _c !== void 0 ? _c : '';
  if (firstName === undefined && lastName === undefined) return fallbackName;
  const middleWithSpace = middleNames && withMiddle ? ` ${formatMiddleNames(middleNames)}` : '';
  const formattedLast = lastInitial ? capitalize((_d = lastName === null || lastName === void 0 ? void 0 : lastName.charAt(0)) !== null && _d !== void 0 ? _d : '') : lastName !== null && lastName !== void 0 ? lastName : '';
  let formattedTitle = '';

  if (withTitle && title) {
    const titleNeedsUppercase = title !== MedicalTitleName.PHLEBOTOMIST && Object.values(MedicalTitleName).includes(title);
    const casedTitle = titleNeedsUppercase ? title.toUpperCase() : capitalize(title);
    formattedTitle = `, ${casedTitle}`;
  }

  return `${firstName !== null && firstName !== void 0 ? firstName : ''}${middleWithSpace} ${formattedLast}${formattedTitle}`;
};
const formatPolicyHolderFullName = (holderInfo, withRelationship = false, fallbackName = 'Unknown Name') => {
  var _a;

  const {
    firstName,
    lastName
  } = holderInfo !== null && holderInfo !== void 0 ? holderInfo : {};
  if (firstName === undefined && lastName === undefined) return fallbackName;
  const formattedRelationship = withRelationship ? ` - ${capitalize((_a = holderInfo === null || holderInfo === void 0 ? void 0 : holderInfo.relationship) !== null && _a !== void 0 ? _a : 'Other')}` : '';
  return `${firstName !== null && firstName !== void 0 ? firstName : ''} ${lastName !== null && lastName !== void 0 ? lastName : ''}${formattedRelationship}`;
};
/**
 * Example: Smith, John
 */

const formatNameLastFirst = (user, withMiddle = false, fallbackName = 'Unknown Name') => {
  var _a;

  const {
    firstName,
    middleNames,
    lastName
  } = (_a = user === null || user === void 0 ? void 0 : user.info) !== null && _a !== void 0 ? _a : {};
  if (!firstName && !lastName) return fallbackName;
  const middleWithSpace = middleNames && withMiddle ? ` ${formatMiddleNames(middleNames)}` : '';
  return `${lastName}, ${firstName}${middleWithSpace}`;
};
/**
 * Example: J. Smith
 */

const formatNameFirstInitial = (user, fallbackName = 'Unknown Name') => {
  var _a;

  const {
    firstName,
    lastName
  } = (_a = user === null || user === void 0 ? void 0 : user.info) !== null && _a !== void 0 ? _a : {};
  if (!firstName || !lastName) return fallbackName;
  return `${firstName[0]}. ${lastName}`;
};
/**
 * Return type is possibly undefined if value passed is possibly undefined.
 *
 * Validation should occur before storage of phone number objects. It is not
 * the responsibility of the formatter to throw errors for invalid phone
 * numbers.
 */

const formatPhoneNumber = phone => {
  if (!phone) return undefined;
  if (!phone.number) return '';
  const fullNumber = `${phone.callingCode}${phone.number}`;
  const phoneNumber = parsePhoneNumber(fullNumber);

  if (phoneNumber.country === 'US') {
    return phoneNumber.formatNational();
  }

  return phoneNumber.formatInternational();
};
const formatUserInitials = user => {
  if (typeof user === 'string') return user.charAt(0);
  if ((user === null || user === void 0 ? void 0 : user.info) === undefined) return null;
  const firstInitial = user.info.firstName.charAt(0);
  const lastInitial = user.info.lastName.charAt(0);
  return `${firstInitial}${lastInitial}`;
};
/**
 * Format the title of a nurse for use on the dispatch steps. If the title is
 * three characters or less, return the title in all caps. Otherwise, return
 * the title in title case.
 */

const formatNurseTitle = title => {
  if (title.length <= 3) return title.toUpperCase();
  return capitalize(title);
};

export { formatFullName, formatNameFirstInitial, formatNameLastFirst, formatNurseTitle, formatPhoneNumber, formatPolicyHolderFullName, formatUserInitials };
